import React, { useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { getAccessoryPurchase, updateAccessoryPurchase } from '../../actions/accessoryPurchase';
import { 
    ArrowLeft, 
    Edit, 
    MapPin, 
    Package, 
    User, 
    Calendar, 
    ShoppingCart,
    CheckCircle,
    Clock,
    Truck,
    XCircle,
    DollarSign
} from 'lucide-react';

const AccessoryPurchase = ({ getAccessoryPurchase, updateAccessoryPurchase, accessoryPurchase, loading }) => {
    const { id } = useParams();

    useEffect(() => {
        if (id) {
            getAccessoryPurchase(id);
        }
    }, [id]);

    const handleStatusUpdate = async (newStatus) => {
        try {
            await updateAccessoryPurchase(id, { 
                status: newStatus,
                delivered: newStatus === 'delivered'
            });
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'processing': return <Clock size={20} className="text-warning" />;
            case 'out of delivery': return <Truck size={20} className="text-info" />;
            case 'delivered': return <CheckCircle size={20} className="text-success" />;
            case 'refunded': return <DollarSign size={20} className="text-secondary" />;
            case 'canceled': return <XCircle size={20} className="text-danger" />;
            default: return <Package size={20} className="text-muted" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'processing': return 'warning';
            case 'out of delivery': return 'info';
            case 'delivered': return 'success';
            case 'refunded': return 'secondary';
            case 'canceled': return 'danger';
            default: return 'secondary';
        }
    };

    if (loading) {
        return (
            <div className="container-fluid py-4">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading accessory purchase details...</p>
                </div>
            </div>
        );
    }

    if (!accessoryPurchase) {
        return (
            <div className="container-fluid py-4">
                <div className="text-center py-5">
                    <Package size={64} className="text-muted mb-3" />
                    <h4 className="text-muted mb-3">Accessory Purchase Not Found</h4>
                    <p className="text-muted mb-4">The requested accessory purchase could not be found.</p>
                    <Link to="/inventory/v1.2/accessory-purchases" className="btn btn-primary">
                        <ArrowLeft size={18} className="me-2" />
                        Back to Purchases
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card shadow-sm border-0" style={{ 
                            backgroundColor: '#F8F9FA', 
                            borderRadius: '12px',
                            padding: '24px'
                        }}>
                            <div className="d-flex align-items-center">
                                <Link 
                                    to="/inventory/v1.2/accessory-purchases"
                                    className="btn btn-light rounded-circle me-3 d-flex align-items-center justify-content-center"
                                    style={{ 
                                        width: '40px', 
                                        height: '40px',
                                        border: '1px solid #E9ECEF',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    <ArrowLeft size={18} className="text-dark" />
                                </Link>
                                <div className="flex-grow-1">
                                    <h1 className="h4 mb-1 fw-bold text-dark" style={{ fontSize: '22px' }}>
                                        Accessory Purchase Details
                                    </h1>
                                    <p className="text-muted mb-0" style={{ fontSize: '15px' }}>
                                        Purchase ID: {accessoryPurchase._id}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    {/* Main Content */}
                    <div className="col-lg-8">
                        {/* Purchase Information */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header bg-white border-bottom">
                                <h5 className="card-title mb-0">
                                    <Package size={20} className="me-2 text-primary" />
                                    Purchase Information
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <Package size={18} className="text-muted me-3" />
                                            <div>
                                                <div className="small text-muted">Accessory</div>
                                                <div className="fw-bold">
                                                    {accessoryPurchase.accessory?.name || 'Unknown Accessory'}
                                                </div>
                                                {accessoryPurchase.accessory?.brand && (
                                                    <div className="small text-muted">
                                                        Brand: {accessoryPurchase.accessory.brand}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <ShoppingCart size={18} className="text-muted me-3" />
                                            <div>
                                                <div className="small text-muted">Quantity</div>
                                                <div className="fw-bold">{accessoryPurchase.quantity}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <User size={18} className="text-muted me-3" />
                                            <div>
                                                <div className="small text-muted">Order ID</div>
                                                <div className="fw-bold">{accessoryPurchase.order.orderId || 'N/A'}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="d-flex align-items-center">
                                            <Calendar size={18} className="text-muted me-3" />
                                            <div>
                                                <div className="small text-muted">Created</div>
                                                <div className="fw-bold">
                                                    {new Date(accessoryPurchase.createdAt).toLocaleDateString()}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Delivery Information */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header bg-white border-bottom">
                                <h5 className="card-title mb-0">
                                    <MapPin size={20} className="me-2 text-primary" />
                                    Delivery Information
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-12">
                                        <div className="d-flex align-items-start">
                                            <MapPin size={18} className="text-muted me-3 mt-1" />
                                            <div className="flex-grow-1">
                                                <div className="small text-muted">Delivery Address</div>
                                                <div className="fw-bold">
                                                    {accessoryPurchase.deliveryAddress?.fullAddress || 'No address provided'}
                                                </div>
                                                {accessoryPurchase.deliveryAddress?.addressId && (
                                                    <div className="small text-muted mt-1">
                                                        Address ID: {accessoryPurchase.deliveryAddress.addressId}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {accessoryPurchase.deliveryAddress?.location && (
                                        <div className="col-md-6">
                                            <div className="d-flex align-items-center">
                                                <MapPin size={18} className="text-muted me-3" />
                                                <div>
                                                    <div className="small text-muted">Coordinates</div>
                                                    <div className="fw-bold">
                                                        {accessoryPurchase.deliveryAddress.location.coordinates[0]}, {accessoryPurchase.deliveryAddress.location.coordinates[1]}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="col-lg-4">
                        {/* Status Card */}
                        <div className="card shadow-sm mb-4">
                            <div className="card-header bg-white border-bottom">
                                <h6 className="card-title mb-0">Status & Actions</h6>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <div className="d-flex align-items-center mb-2">
                                        {getStatusIcon(accessoryPurchase.status)}
                                        <span className="ms-2 fw-bold">Current Status</span>
                                    </div>
                                    <span className={`badge bg-${getStatusColor(accessoryPurchase.status)} fs-6`}>
                                        {accessoryPurchase.status || 'Unknown'}
                                    </span>
                                </div>

                                <div className="mb-3">
                                    <div className="d-flex align-items-center">
                                        <CheckCircle size={18} className="text-muted me-2" />
                                        <span className="me-2">Delivered:</span>
                                        <span className={`badge ${accessoryPurchase.delivered ? 'bg-success' : 'bg-secondary'}`}>
                                            {accessoryPurchase.delivered ? 'Yes' : 'No'}
                                        </span>
                                    </div>
                                </div>

                                {/* Status Update Buttons */}
                                <div className="d-grid gap-2">
                                    {accessoryPurchase.status !== 'processing' && (
                                        <button 
                                            className="btn btn-outline-warning btn-sm"
                                            onClick={() => handleStatusUpdate('processing')}
                                        >
                                            <Clock size={16} className="me-2" />
                                            Set to Processing
                                        </button>
                                    )}
                                    {accessoryPurchase.status !== 'out of delivery' && (
                                        <button 
                                            className="btn btn-outline-info btn-sm"
                                            onClick={() => handleStatusUpdate('out of delivery')}
                                        >
                                            <Truck size={16} className="me-2" />
                                            Out for Delivery
                                        </button>
                                    )}
                                    {accessoryPurchase.status !== 'delivered' && (
                                        <button 
                                            className="btn btn-outline-success btn-sm"
                                            onClick={() => handleStatusUpdate('delivered')}
                                        >
                                            <CheckCircle size={16} className="me-2" />
                                            Mark as Delivered
                                        </button>
                                    )}
                                    {accessoryPurchase.status !== 'canceled' && (
                                        <button 
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={() => handleStatusUpdate('canceled')}
                                        >
                                            <XCircle size={16} className="me-2" />
                                            Cancel Order
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Purchase Details */}
                        <div className="card shadow-sm">
                            <div className="card-header bg-white border-bottom">
                                <h6 className="card-title mb-0">Purchase Details</h6>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <div className="small text-muted">Purchase ID</div>
                                    <div className="fw-bold small">{accessoryPurchase._id}</div>
                                </div>
                                <div className="mb-3">
                                    <div className="small text-muted">Created</div>
                                    <div className="fw-bold">
                                        {new Date(accessoryPurchase.createdAt).toLocaleString()}
                                    </div>
                                </div>
                                {accessoryPurchase.updatedAt && (
                                    <div className="mb-3">
                                        <div className="small text-muted">Last Updated</div>
                                        <div className="fw-bold">
                                            {new Date(accessoryPurchase.updatedAt).toLocaleString()}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

AccessoryPurchase.propTypes = {
    getAccessoryPurchase: PropTypes.func.isRequired,
    updateAccessoryPurchase: PropTypes.func.isRequired,
    accessoryPurchase: PropTypes.object,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => ({
    accessoryPurchase: state.accessoryPurchase.accessoryPurchase,
    loading: state.accessoryPurchase.loading
});

export default connect(mapStateToProps, { getAccessoryPurchase, updateAccessoryPurchase })(AccessoryPurchase);
